// addCte — VQE-Edit edit-action: add a new CTE binding via SR-Edit's
// `add-cte` op. SR-Edit synthesizes the leading `WITH [RECURSIVE]` keyword
// when the statement has no WITH clause yet; otherwise the new entry is
// inserted as a comma-separated peer of existing bindings.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove/rename CTE.

import { commitEdit, type CommitEditCtx } from '../commitEdit';

export interface AddCteOptions {
  /** Optional column list (e.g. `['c1', 'c2']`). When set, emitted as `name (c1, c2) AS (...)`. */
  readonly columns?: readonly string[];
  /** Whether to mark the WITH clause as RECURSIVE. Only honoured when no WITH clause exists yet. */
  readonly isRecursive?: boolean;
  /** Insert AFTER this 0-based index in the existing CTE list. Default: append. */
  readonly afterIndex?: number;
}

/**
 * Add a CTE binding. `name` is the CTE's identifier (caller is responsible for
 * dialect-appropriate quoting). `bodyText` is the SELECT/DML body WITHOUT the
 * surrounding parentheses — SR-Edit wraps it in `(...)`.
 */
export async function addCte(
  name: string,
  bodyText: string,
  ctx: CommitEditCtx,
  options: AddCteOptions = {},
): Promise<void> {
  const trimmedName = name.trim();
  if (trimmedName.length === 0) {
    throw new Error('addCte: name must not be empty');
  }
  const trimmedBody = bodyText.trim();
  if (trimmedBody.length === 0) {
    throw new Error('addCte: bodyText must not be empty');
  }
  await commitEdit(
    {
      op: 'add-cte',
      name: trimmedName,
      bodyText: trimmedBody,
      ...(options.columns && options.columns.length > 0 ? { columns: options.columns } : {}),
      ...(options.isRecursive ? { isRecursive: true } : {}),
      ...(options.afterIndex !== undefined ? { afterIndex: options.afterIndex } : {}),
    },
    ctx,
  );
}
